import React from "react";
import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AssistantWidget } from "@/components/AssistantWidget";
// Pages
import LandingPage from "@/pages/LandingPage";
import EventWizard from "@/pages/EventWizard";
import ImportSchedulePage from "@/pages/ImportSchedulePage";
import ScheduleView from "@/pages/ScheduleView";
import SharedScheduleView from "@/pages/SharedScheduleView";
import MySchedulesPage from "@/pages/MySchedulesPage";
import BracketView from "@/pages/BracketView";
import BracketLibrary from "@/pages/BracketLibrary";
import SharedBracketPage from "@/pages/SharedBracketPage";
import TournamentDashboard from "@/pages/TournamentDashboard";
import ExportPage from "@/pages/ExportPage";
import LoginPage from "@/pages/LoginPage";
import GoogleCallbackPage from "@/pages/GoogleCallbackPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import UserManual from "@/pages/UserManual";
import OfficialsManual from "@/pages/OfficialsManual";
import MySupportTickets from "@/pages/MySupportTickets";
import NewSupportTicket from "@/pages/NewSupportTicket";
import SupportTicketDetail from "@/pages/SupportTicketDetail";
import AdminSupportTickets from "@/pages/AdminSupportTickets";
import PricingPage from "@/pages/PricingPage";
import SportLandingPage from "@/pages/SportLandingPage";
import AdminDashboard from "@/pages/AdminDashboard";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import StatsHub from "@/pages/StatsHub";
import StatsSetup from "@/pages/StatsSetup";
import StatsGame from "@/pages/StatsGame";
import StatsSummary from "@/pages/StatsSummary";
import SeasonTotals from "@/pages/SeasonTotals";
import GameFlow from "@/pages/GameFlow";
import LineupRankings from "@/pages/LineupRankings";
import PerAndAwards from "@/pages/PerAndAwards";
import PublicLeaderboard from "@/pages/PublicLeaderboard";
import MatchStats from "@/pages/MatchStats";
import PlayerReport from "@/pages/PlayerReport";
import BoxScoreImport from "@/pages/BoxScoreImport";
import TeamRosterPage from "@/pages/TeamRosterPage";
import JoinGamePage from "@/pages/JoinGamePage";
import SettingsPage from "@/pages/Settings";
import CareerStatSheet from "@/pages/CareerStatSheet";
import PublicTeamDetail from "@/pages/PublicTeamDetail";
import PrintStatSheetPage from "@/pages/PrintStatSheetPage";
import OfficialsHub from "@/pages/OfficialsHub";
import OfficialsPortal from "@/pages/OfficialsPortal";
import OfficialsJoin from "@/pages/OfficialsJoin";
import OfficialsRespond from "@/pages/OfficialsRespond";
import BillingPage from "@/pages/BillingPage";
import BillingResult from "@/pages/BillingResult";
import UserMenu from "@/components/UserMenu";

// Components
import GracePeriodBanner from "@/components/GracePeriodBanner";
import UpdateAvailableBanner from "@/components/UpdateAvailableBanner";
import MobileBottomNav, { isMobileNavVisiblePath } from "@/components/MobileBottomNav";

// Context
import { EventProvider } from "@/context/EventContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { captureAttribution, trackPageView } from "@/lib/growthTracking";

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google" element={<GoogleCallbackPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/basketball-league-software" element={<SportLandingPage />} />
      <Route path="/soccer-league-software" element={<SportLandingPage />} />
      <Route path="/volleyball-league-software" element={<SportLandingPage />} />
      <Route path="/softball-league-software" element={<SportLandingPage />} />
      <Route path="/flag-football-league-software" element={<SportLandingPage />} />
      <Route path="/netball-league-software" element={<SportLandingPage />} />
      <Route path="/cricket-league-software" element={<SportLandingPage />} />
      <Route path="/billing" element={<BillingPage />} />
      <Route path="/billing/result" element={<BillingResult />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/help" element={<UserManual />} />
      <Route path="/manual" element={<UserManual />} />
      <Route path="/manual/officials" element={<OfficialsManual />} />
      <Route path="/help/officials" element={<OfficialsManual />} />
      <Route path="/support" element={<MySupportTickets />} />
      <Route path="/support/new" element={<NewSupportTicket />} />
      <Route path="/support/:ticketId" element={<SupportTicketDetail />} />
      <Route path="/admin/support" element={<AdminSupportTickets />} />
      <Route path="/admin/support/:ticketId" element={<SupportTicketDetail adminMode={true} />} />
      <Route path="/create" element={<EventWizard />} />
      <Route path="/import" element={<ImportSchedulePage />} />
      <Route path="/my-schedules" element={<MySchedulesPage />} />
      <Route path="/schedule/:eventId" element={<ScheduleView />} />
      <Route path="/events/:eventId/matches/:matchId/print-stat-sheet" element={<PrintStatSheetPage />} />
      <Route path="/events/:eventId/print-stat-sheets" element={<PrintStatSheetPage />} />
      <Route path="/share/:shareToken" element={<SharedScheduleView />} />
      <Route path="/share/:shareToken/team/:teamId" element={<PublicTeamDetail />} />
      <Route path="/share/:shareToken/:tab" element={<SharedScheduleView />} />
      <Route path="/bracket/:eventId" element={<BracketView />} />
      <Route path="/bracket-library" element={<BracketLibrary />} />
      <Route path="/b/:shareId" element={<SharedBracketPage />} />
      <Route path="/dashboard/:eventId" element={<TournamentDashboard />} />
      <Route path="/export/:eventId" element={<ExportPage />} />
      <Route path="/stats" element={<StatsHub />} />
      <Route path="/stats/setup" element={<StatsSetup />} />
      <Route path="/stats/game/:gameId" element={<StatsGame />} />
      <Route path="/stats/summary/:gameId" element={<StatsSummary />} />
      <Route path="/stats/season" element={<SeasonTotals />} />
      <Route path="/stats/flow/:gameId" element={<GameFlow />} />
      <Route path="/stats/rankings" element={<LineupRankings />} />
      <Route path="/stats/awards" element={<PerAndAwards />} />
      <Route path="/stats/leaderboard/:shareToken" element={<PublicLeaderboard />} />
      <Route path="/stats/leaderboard/:shareToken/career" element={<CareerStatSheet />} />
      <Route path="/stats/match/:matchId" element={<MatchStats />} />
      <Route path="/stats/player/:playerId" element={<PlayerReport />} />
      <Route path="/stats/import/:gameId" element={<BoxScoreImport />} />
      <Route path="/team/:teamToken" element={<TeamRosterPage />} />
      <Route path="/join" element={<JoinGamePage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/officials" element={<OfficialsHub />} />
      <Route path="/officials/portal" element={<OfficialsPortal />} />
      <Route path="/officials/join/:token" element={<OfficialsJoin />} />
      <Route path="/officials/respond/:token" element={<OfficialsRespond />} />
    </Routes>
  );
}

function AppContent() {
  const { isInGracePeriod, daysUntilDowngrade, isAuthenticated } = useAuth();
  const location = useLocation();

  // Meta Pixel: react-router does client-side nav, so the initial PageView
  // fired in index.html captures only the first page a user lands on.
  // Fire a PageView on every subsequent route change so the pixel sees the
  // full SPA journey (critical for retargeting + conversion attribution).
  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
    trackPageView(location.pathname);
  }, [location.pathname]);
  // Pages that render their own inline UserMenu inside their header should
  // hide the global floating one so the two don't stack. Includes:
  //  - Live stat tracker (full-screen deck)
  //  - Schedule / league view (has its own top bar with Share / Visibility / Score buttons)
  const hideUserMenu =
    /^\/stats\/(game|flow)\//.test(location.pathname) ||
    /^\/schedule\//.test(location.pathname) ||
    /^\/officials(\/|$)/.test(location.pathname);

  const showMobileNav = isAuthenticated && isMobileNavVisiblePath(location.pathname);

  return (
    <div className={`app-container ${showMobileNav ? 'pb-16 sm:pb-0' : ''}`}>
      <UpdateAvailableBanner />
      {isInGracePeriod && (
        <GracePeriodBanner daysLeft={daysUntilDowngrade} />
      )}
      <AppRouter />
      {!hideUserMenu && <UserMenu />}
      <MobileBottomNav />
      <Toaster position="top-right" richColors />
      <AssistantWidget />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <EventProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </EventProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
